import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, ChevronRight } from "lucide-react";
import { Seo, SITE_URL, SITE_NAME, PHONE, EMAIL } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { CITIES, comboPath } from "../data/cities";
import { Testimonials } from "../components/Testimonials";

export default function ServicePage({ service }) {
  const faqLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: service.name,
        provider: { "@type": "Organization", name: SITE_NAME, telephone: PHONE, email: EMAIL },
        areaServed: { "@type": "AdministrativeArea", name: "Ontario, Canada" },
        url: `${SITE_URL}/security-services/${service.slug}`,
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  const featuredCities = CITIES.slice(0, 18);

  return (
    <div data-testid={`service-page-${service.slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title={`${service.name} Ontario | 1st Nations Security`}
        description={service.meta}
        path={`/security-services/${service.slug}`}
        jsonLd={faqLd}
      />
      <nav data-testid="service-breadcrumb" className="text-xs text-slate-500 mb-6 flex items-center gap-1.5">
        <Link to="/" className="hover:text-amber-300">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/security-services" className="hover:text-amber-300">Security Services</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{service.name}</span>
      </nav>

      <div className="flex items-center gap-4 mb-6">
        <service.icon className="h-12 w-12 text-amber-400" />
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Ontario Security Services</p>
      </div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        {service.name} in Ontario
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">{service.intro}</p>
      {service.body.map((p, i) => (
        <p key={i} className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">{p}</p>
      ))}

      <section data-testid="service-included-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          What Our {service.name} Includes
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 max-w-4xl">
          {service.points.map((pt) => (
            <div key={pt} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/60 px-5 py-4">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200">{pt}</span>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="service-cities-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          {service.name} Across Ontario
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
          We provide {service.name.toLowerCase()} in every region of Ontario. Select your city for local security
          guard services:
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {featuredCities.map((c) => (
            <Link
              key={c.slug}
              to={comboPath(c, service)}
              data-testid={`service-city-link-${c.slug}`}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
            >
              <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" /> {service.name} {c.name}
            </Link>
          ))}
        </div>
        <Link to="/ontario-service-areas" data-testid="service-all-cities-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 hover:text-amber-300">
          View all Ontario service areas <ChevronRight className="h-4 w-4" />
        </Link>
      </section>

      <Testimonials serviceSlug={service.slug} />

      <section data-testid="service-faq-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {service.name} — FAQs
        </h2>
        <div className="space-y-4 max-w-3xl">
          {service.faqs.map((f, i) => (
            <details key={i} data-testid={`service-faq-${i}`} className="group rounded-xl border border-white/10 bg-slate-900/60 p-5">
              <summary className="cursor-pointer text-base font-semibold text-white list-none flex items-center justify-between gap-3">
                {f.q}
                <ChevronRight className="h-4 w-4 text-amber-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CtaBand
        heading={`Hire ${service.name} in Ontario`}
        sub={`Call our 24/7 dispatch at ${PHONE} or email ${EMAIL} to discuss ${service.name.toLowerCase()} for your site, business or event — anywhere in Ontario.`}
      />
    </div>
  );
}
